import type { LateFeePolicy, OrganizationResponse } from '../guards/organization-context.service';
import type { PaymentReadiness } from '../services/payments/stripe-connect.service';

/** Every onboarding step. Add, remove or reorder steps in SETUP_GUIDE_STEPS below. */
export type SetupStepId =
  | 'workspace-basics'
  | 'online-payments'
  | 'accounting'
  | 'late-fees'
  | 'first-property'
  | 'first-lease'
  | 'owners'
  | 'team'
  | 'maintenance-companies';

/** One control the walkthrough points at: the element carrying `data-guide="<id>"` on the step's page. */
export type GuideTarget = {
  id: string;
  tipKey: string;
};

export type SetupGuideStep = {
  id: SetupStepId;
  icon: string;
  titleKey: string;
  whyKey: string;
  optional: boolean;
  route: string;
  queryParams?: Record<string, string>;
  /** Needed to act on the step. Defaults to the permissions for visiting `route`. */
  permissions?: readonly string[];
  planFeature?: string;
  planCapacity?: 'property' | 'staff';
  targets: readonly GuideTarget[];
};

export const SETUP_GUIDE_STEPS: readonly SetupGuideStep[] = [
  {
    id: 'workspace-basics',
    icon: 'tune',
    titleKey: 'setupGuide.step.workspaceBasics.title',
    whyKey: 'setupGuide.step.workspaceBasics.why',
    optional: false,
    route: '/settings',
    queryParams: { section: 'organisation' },
    permissions: ['ORG_EDIT_SETTINGS'],
    targets: [
      { id: 'settings-currency', tipKey: 'setupGuide.tip.currency' },
      { id: 'settings-time-zone', tipKey: 'setupGuide.tip.timeZone' },
      { id: 'settings-save', tipKey: 'setupGuide.tip.saveOrganisation' },
    ],
  },
  {
    id: 'online-payments',
    icon: 'payments',
    titleKey: 'setupGuide.step.onlinePayments.title',
    whyKey: 'setupGuide.step.onlinePayments.why',
    optional: false,
    route: '/payments/stripe-connect',
    targets: [{ id: 'stripe-connect', tipKey: 'setupGuide.tip.stripeConnect' }],
  },
  {
    id: 'accounting',
    icon: 'account_balance',
    titleKey: 'setupGuide.step.accounting.title',
    whyKey: 'setupGuide.step.accounting.why',
    optional: false,
    route: '/accounting',
    permissions: ['ACCOUNTING_CONFIGURE'],
    planFeature: 'ACCOUNTING',
    targets: [{ id: 'accounting-initialize', tipKey: 'setupGuide.tip.accountingInitialize' }],
  },
  {
    id: 'late-fees',
    icon: 'schedule',
    titleKey: 'setupGuide.step.lateFees.title',
    whyKey: 'setupGuide.step.lateFees.why',
    optional: false,
    route: '/settings',
    queryParams: { section: 'rentLateFeePolicy' },
    permissions: ['ORG_EDIT_SETTINGS'],
    targets: [
      { id: 'late-fee-enable', tipKey: 'setupGuide.tip.lateFeeEnable' },
      { id: 'late-fee-grace', tipKey: 'setupGuide.tip.lateFeeGrace' },
      { id: 'settings-save', tipKey: 'setupGuide.tip.saveLateFee' },
    ],
  },
  {
    id: 'first-property',
    icon: 'apartment',
    titleKey: 'setupGuide.step.firstProperty.title',
    whyKey: 'setupGuide.step.firstProperty.why',
    optional: false,
    route: '/properties/add',
    planCapacity: 'property',
    queryParams: { mode: 'select' },
    targets: [
      { id: 'property-add-mode', tipKey: 'setupGuide.tip.propertyMode' },
      { id: 'property-add-continue', tipKey: 'setupGuide.tip.propertyContinue' },
    ],
  },
  {
    id: 'first-lease',
    icon: 'contract',
    titleKey: 'setupGuide.step.firstLease.title',
    whyKey: 'setupGuide.step.firstLease.why',
    optional: false,
    route: '/leases',
    permissions: ['LEASE_CREATE'],
    targets: [{ id: 'lease-create', tipKey: 'setupGuide.tip.leaseCreate' }],
  },
  {
    id: 'owners',
    icon: 'person_add',
    titleKey: 'setupGuide.step.owners.title',
    whyKey: 'setupGuide.step.owners.why',
    optional: true,
    route: '/owners',
    permissions: ['OWNER_MANAGE'],
    targets: [{ id: 'owner-add', tipKey: 'setupGuide.tip.ownerAdd' }],
  },
  {
    id: 'team',
    icon: 'group_add',
    titleKey: 'setupGuide.step.team.title',
    whyKey: 'setupGuide.step.team.why',
    optional: true,
    route: '/settings',
    queryParams: { section: 'teamRoles' },
    permissions: ['MEMBERSHIP_VIEW', 'MEMBERSHIP_INVITE'],
    planCapacity: 'staff',
    targets: [{ id: 'team-invite', tipKey: 'setupGuide.tip.teamInvite' }],
  },
  {
    id: 'maintenance-companies',
    icon: 'engineering',
    titleKey: 'setupGuide.step.maintenanceCompanies.title',
    whyKey: 'setupGuide.step.maintenanceCompanies.why',
    optional: true,
    route: '/maintenance/companies',
    permissions: ['MAINTENANCE_SETTINGS_MANAGE'],
    targets: [{ id: 'maintenance-company-invite', tipKey: 'setupGuide.tip.maintenanceCompanyInvite' }],
  },
];

export function setupGuideStep(id: string | null | undefined): SetupGuideStep | null {
  return SETUP_GUIDE_STEPS.find(step => step.id === id) ?? null;
}

/** true = done, false = not done, null/missing = could not be determined (always shown as to do). */
export type SetupFacts = Partial<Record<SetupStepId, boolean | null>>;

export type SetupStepStatus = 'done' | 'todo' | 'optional';

export type SetupStepView = {
  step: SetupGuideStep;
  done: boolean;
  status: SetupStepStatus;
};

export function evaluateSetupSteps(steps: readonly SetupGuideStep[], facts: SetupFacts): SetupStepView[] {
  return steps.map(step => {
    const done = facts[step.id] === true;
    return { step, done, status: done ? 'done' : step.optional ? 'optional' : 'todo' };
  });
}

export function setupProgress(views: readonly SetupStepView[]): { done: number; total: number; complete: boolean } {
  const required = views.filter(view => !view.step.optional);
  const done = required.filter(view => view.done).length;
  return { done, total: required.length, complete: required.length > 0 && done === required.length };
}

// Completion checks: each reads data an existing API already returns. Unknown input gives null, never "done".

export function workspaceBasicsDone(organization: OrganizationResponse | null | undefined): boolean | null {
  if (!organization) return null;
  return !!organization.currency?.trim() && !!organization.timeZone?.trim();
}

export function onlinePaymentsDone(status: PaymentReadiness | null | undefined): boolean | null {
  if (!status) return null;
  return status.paymentsReady === true && status.chargesEnabled === true && status.payoutsEnabled === true;
}

/** Mirrors the Accounting page's read-only setup check: defaults exist once there are ledger accounts and bank registers. */
export function accountingDefaultsDone(accounts: readonly unknown[] | null | undefined, bankAccounts: readonly unknown[] | null | undefined): boolean | null {
  if (!Array.isArray(accounts) || !Array.isArray(bankAccounts)) return null;
  return accounts.length > 0 && bankAccounts.length > 0;
}

/** A saved policy, even a disabled one, is a decision; the server returns no policy until one is saved. */
export function lateFeePolicyDone(organization: OrganizationResponse | null | undefined): boolean | null {
  if (!organization) return null;
  const policy: LateFeePolicy | null | undefined = organization.defaultLateFeePolicy;
  return !!policy && policy.enabled != null;
}

export function hasAny(count: number | null | undefined): boolean | null {
  return typeof count === 'number' && Number.isFinite(count) ? count > 0 : null;
}

/** The owner always counts as one active member, so a teammate means a second member or a pending invite. */
export function teamInvitedDone(activeMembers: number | null | undefined, invitedMembers: number | null | undefined): boolean | null {
  const active = typeof activeMembers === 'number' ? activeMembers : null;
  const invited = typeof invitedMembers === 'number' ? invitedMembers : null;
  if ((active ?? 0) > 1 || (invited ?? 0) > 0) return true;
  return active == null || invited == null ? null : false;
}

export const SETUP_GUIDE_HIDDEN_STORAGE_PREFIX = 'apexBackofficeSetupGuideHidden.v1:';
